import { Check, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Dropdown, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

export interface Option {
  value: string;
  label: string;
  sublabel?: string;
}

export interface MultiSelectDropdownProps {
  options: Option[];
  value: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function MultiSelectDropdown({
  options,
  value,
  onChange,
  placeholder = 'Select options...',
  disabled = false,
  className = '',
}: MultiSelectDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(
    option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (option.sublabel &&
        option.sublabel.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  // Ensure value only contains valid option values
  useEffect(() => {
    // Get selected options details
    setSelectedOptions(options.filter(option => value.includes(option.value)));
  }, [options, value, onChange]);

  // Handle option toggle
  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];

    onChange(newValue);
  };

  // Handle select all
  const handleSelectAll = () => {
    const allValues = filteredOptions.map(opt => opt.value);
    onChange(allValues);
  };

  // Handle clear all
  const handleClearAll = () => {
    onChange([]);
  };

  // Handle remove single selected item
  const handleRemoveSelected = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optionValue));
  };

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isOpen]);

  const getDisplayText = () => {
    if (selectedOptions.length === 0) return placeholder;
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    return `${selectedOptions.length} ${t('common.selected')}`;
  };

  return (
    <div className={`position-relative ${className}`} ref={dropdownRef}>
      <Dropdown show={isOpen} onToggle={setIsOpen}>
        <Dropdown.Toggle
          variant="outline-secondary"
          className="w-100 d-flex align-items-center justify-content-between text-start"
          disabled={disabled}
          style={{
            minHeight: '38px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <span className={selectedOptions.length === 0 ? 'text-muted' : ''}>
            {getDisplayText()}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="w-100 shadow-lg"
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            minWidth: '280px',
          }}
        >
          {/* Search input */}
          <div className="sticky-top bg-white border-bottom p-2">
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="mb-2"
              size="sm"
            />

            {/* Quick actions */}
            <div className="d-flex gap-2">
              <Button
                variant="link"
                size="sm"
                className="text-decoration-none p-0"
                onClick={handleSelectAll}
              >
                {t('common.selectAll')}
              </Button>
              <span className="text-muted">•</span>
              <Button
                variant="link"
                size="sm"
                className="text-decoration-none p-0"
                onClick={handleClearAll}
              >
                {t('common.clearAll')}
              </Button>
            </div>
          </div>

          {/* Options list */}
          <div className="p-2">
            {filteredOptions.length === 0 ? (
              <div className="text-center text-muted py-3">
                {t('common.noOptionsFound')}
              </div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  className="d-flex align-items-center p-2 rounded hover-bg-light"
                  style={{
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onClick={() => handleToggleOption(option.value)}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = '#f8f9fa')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = 'transparent')
                  }
                >
                  <Form.Check
                    type="checkbox"
                    checked={value.includes(option.value)}
                    onChange={() => {}}
                    className="me-3"
                    style={{ pointerEvents: 'none' }}
                  />
                  <div className="flex-grow-1">
                    <div className="fw-medium">{option.label}</div>
                    {option.sublabel && (
                      <div className="text-muted small">{option.sublabel}</div>
                    )}
                  </div>
                  {value.includes(option.value) && (
                    <Check size={16} className="text-primary ms-2" />
                  )}
                </div>
              ))
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>

      {/* Selected items badges (shown below dropdown when items are selected) */}
      {selectedOptions.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mt-2">
          {selectedOptions.map(option => (
            <Badge
              key={option.value}
              bg="secondary"
              className="d-flex align-items-center gap-1 py-1 px-2"
              style={{ fontSize: '0.875rem' }}
            >
              {option.label}
              <X
                size={14}
                className="ms-1"
                style={{ cursor: 'pointer' }}
                onClick={e => handleRemoveSelected(option.value, e)}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
